import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Sparkles, Wand2, Save, Copy, Check } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";
import { SEO_DESC_PREFIX, SEO_KW_PREFIX, getSeo } from "../../lib/seo";

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{children}</label>;

const buildKeywords = (title, copy) => {
  const stop = new Set(["the", "and", "for", "with", "your", "you", "our", "win", "a", "an", "to", "of", "in", "on", "is", "this", "that", "are", "be"]);
  const words = `${title} ${copy}`.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((w) => w.length > 3 && !stop.has(w));
  const freq = {};
  words.forEach((w) => (freq[w] = (freq[w] || 0) + 1));
  const top = Object.keys(freq).sort((a, b) => freq[b] - freq[a]).slice(0, 6);
  const base = ["uk prize competition", "win", "free postal entry"];
  return Array.from(new Set([...top, ...base])).slice(0, 8).join(", ");
};

export default function AdminSeo() {
  const [loading, setLoading] = useState(true);
  const [comps, setComps] = useState([]);
  const [selId, setSelId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [keywords, setKeywords] = useState("");

  useEffect(() => {
    api.get("/competitions").then(({ data }) => setComps(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const selectComp = (id) => {
    setSelId(id);
    const c = comps.find((x) => x.id === id);
    if (!c) return;
    const seo = getSeo(c);
    setPrompt(c.prize_title || c.title || "");
    setMetaTitle(seo.title || `${c.title} | Richi£ Rich Competitions`);
    setMetaDesc(seo.desc || (c.description || "").slice(0, 155));
    setKeywords(seo.keywords || "");
  };

  const generate = async () => {
    const p = prompt || (comps.find((c) => c.id === selId)?.title);
    if (!p) { toast.error("Pick a competition or type what it's about first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai/describe", { prompt: `SEO meta for a UK prize competition: ${p}. Write a punchy, search-friendly title and a 150-character meta description.` });
      const title = (data.title || p).slice(0, 60);
      const copy = data.copy || data.description || "";
      setMetaTitle(`${title} | Richi£ Rich Competitions`.slice(0, 65));
      setMetaDesc(copy.replace(/\s+/g, " ").trim().slice(0, 155));
      setKeywords(buildKeywords(title, copy));
      toast.success("AI SEO generated.");
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveToComp = async () => {
    if (!selId) { toast.error("Select a competition to save SEO against."); return; }
    const c = comps.find((x) => x.id === selId);
    if (!c) return;
    setSaving(true);
    try {
      const tags = (c.metadata_tags || []).filter((t) => typeof t === "string" && !t.startsWith(SEO_DESC_PREFIX) && !t.startsWith(SEO_KW_PREFIX) && !t.startsWith("seo_title:"));
      tags.push(`seo_title:${metaTitle}`, `${SEO_DESC_PREFIX}${metaDesc}`, `${SEO_KW_PREFIX}${keywords}`);
      await api.put(`/admin/competitions/${selId}`, { metadata_tags: tags });
      toast.success("SEO saved to competition — it now drives that page's title & description.");
      const { data } = await api.get("/competitions");
      setComps(data || []);
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-seo" className="max-w-3xl">
      <div className="flex items-center gap-2">
        <Search className="h-6 w-6 text-[var(--neon-cyan)]" />
        <h2 className="font-display uppercase text-2xl text-white">AI SEO Studio</h2>
      </div>
      <p className="mt-1 text-sm text-[var(--text-muted)]">Generate search-optimised titles, meta descriptions and keywords. Save them to a competition to drive that page's SEO automatically.</p>

      <NeonCard variant="flow" className="mt-4" innerClassName="p-5 space-y-3">
        <div>
          <Label>Competition</Label>
          <select value={selId} onChange={(e) => selectComp(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="seo-comp-select">
            <option value="">— Select a competition —</option>
            {comps.map((c) => (
              <option key={c.id} value={c.id}>{c.title}{getSeo(c).desc ? "  ✓ SEO set" : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>What is it about? (AI prompt)</Label>
          <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. win a brand new Lamborghini Huracán" className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="seo-prompt" />
        </div>
        <NeonButton variant="outlineCyan" className="w-full" onClick={generate} loading={busy} data-testid="seo-generate"><Wand2 className="h-4 w-4" /> Generate AI SEO</NeonButton>
      </NeonCard>

      {(metaTitle || metaDesc) && (
        <NeonCard variant="cyan" className="mt-4" innerClassName="p-5 space-y-4">
          <div className="flex items-center gap-1 text-xs font-display uppercase tracking-widest glow-cyan"><Sparkles className="h-3.5 w-3.5" /> Generated</div>
          <div>
            <div className="flex items-center justify-between"><Label>Meta title ({metaTitle.length}/65)</Label><button onClick={() => copy(metaTitle, "t")} className="text-[var(--neon-cyan)]">{copied === "t" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</button></div>
            <input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="seo-meta-title" />
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Meta description ({metaDesc.length}/155)</Label><button onClick={() => copy(metaDesc, "d")} className="text-[var(--neon-cyan)]">{copied === "d" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</button></div>
            <textarea value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} rows={3} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="seo-meta-desc" />
          </div>
          <div>
            <Label>Keywords</Label>
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="seo-keywords" />
          </div>
          <NeonButton variant="pink" className="w-full" onClick={saveToComp} loading={saving} data-testid="seo-save"><Save className="h-4 w-4" /> Save SEO to Competition</NeonButton>
          {selId && <p className="text-center text-[11px] text-[var(--text-muted)]">Stored in the shared backend via metadata_tags — the public competition page reads it automatically.</p>}
        </NeonCard>
      )}
    </div>
  );
}
